import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  CardMedia,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import AdminLayout from '../../components/layout/AdminLayout';

interface Product {
  id: number;
  name: string;
  description: string;
  price: number;
  originalPrice?: number;
  category: string;
  stock: number;
  status: 'ACTIVE' | 'INACTIVE' | 'OUT_OF_STOCK';
  imageUrl: string;
  createdAt: string;
  sales: number;
}

const AdminProducts: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  // Mock data for demonstration
  useEffect(() => {
    const mockProducts: Product[] = [
      {
        id: 1,
        name: 'Premium Foam Cleanser',
        description: 'Premium foam cleanser that provides deep cleansing and moisturization simultaneously',
        price: 29000,
        originalPrice: 35000,
        category: 'Cleanser',
        stock: 150,
        status: 'ACTIVE',
        imageUrl: 'https://images.unsplash.com/photo-1556229174-f6a46a04ac10?w=300',
        createdAt: '2024-01-10',
        sales: 245,
      },
      {
        id: 2,
        name: 'Moisturizing Toner',
        description: 'Excellent toner for hydration and soothing skin effects',
        price: 25000,
        category: 'Toner',
        stock: 0,
        status: 'OUT_OF_STOCK',
        imageUrl: 'https://images.unsplash.com/photo-1620916566398-39f1143ab7be?w=300',
        createdAt: '2024-01-08',
        sales: 189,
      },
      {
        id: 3,
        name: 'Anti-Aging Serum',
        description: 'Intensive care serum for wrinkle improvement and elasticity enhancement',
        price: 45000,
        category: 'Serum',
        stock: 75,
        status: 'ACTIVE',
        imageUrl: 'https://images.unsplash.com/photo-1570194065650-d99fb4bedf0a?w=300',
        createdAt: '2024-01-05',
        sales: 156,
      },
    ];

    setTimeout(() => {
      setProducts(mockProducts);
      setLoading(false);
    }, 1000);
  }, []);

  const categories = ['Cleanser', 'Toner', 'Serum', 'Essence', 'Cream'];

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !categoryFilter || product.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const handleEditProduct = (product: Product) => {
    setSelectedProduct(product);
    setEditDialogOpen(true);
  };

  const handleToggleProductStatus = (productId: number) => {
    setProducts(products.map(product =>
      product.id === productId
        ? { ...product, status: product.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE' }
        : product
    ));
  };

  const getStatusChip = (status: string, stock: number) => {
    if (stock === 0) {
      return (
        <Chip
          label="Out of Stock"
          size="small"
          sx={{ backgroundColor: '#F4433620', color: '#F44336', fontWeight: 600 }}
        />
      );
    }
    
    return (
      <Chip
        label={status === 'ACTIVE' ? 'Active' : 'Inactive'}
        size="small"
        sx={{
          backgroundColor: status === 'ACTIVE' ? '#4CAF5020' : '#FF7F2F20',
          color: status === 'ACTIVE' ? '#4CAF50' : '#FF7F2F',
          fontWeight: 600,
        }}
      />
    );
  };

  const formatPrice = (price: number) => {
    return '₩' + new Intl.NumberFormat('ko-KR').format(price);
  };

  if (loading) {
    return (
      <AdminLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress size={60} sx={{ color: '#35C5F0' }} />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: '#333',
            mb: 1,
          }}
        >
          Product Management
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Manage and update online store products
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Search and Filter */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', flex: 1 }}>
          <TextField
            placeholder="Search products..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#666' }} />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: '100%', sm: 250 } }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryFilter}
              label="Category"
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {categories.map((category) => (
                <MenuItem key={category} value={category}>{category}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          sx={{
            backgroundColor: '#35C5F0',
            '&:hover': { backgroundColor: '#2ba3cc' },
          }}
        >
          Add New Product
        </Button>
      </Box>

      {/* Products Grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
          },
          gap: 3,
        }}
      >
        {filteredProducts.map((product) => (
          <Card
            key={product.id}
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                },
              }}
            >
              <CardMedia
                component="img"
                height="200"
                image={product.imageUrl}
                alt={product.name}
                sx={{
                  objectFit: 'cover',
                  filter: product.status === 'INACTIVE' ? 'grayscale(100%)' : 'none',
                }}
              />
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      fontSize: '1rem',
                      lineHeight: 1.3,
                      flex: 1,
                    }}
                  >
                    {product.name}
                  </Typography>
                  {getStatusChip(product.status, product.stock)}
                </Box>

                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{
                    mb: 2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {product.description}
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Category: {product.category} | Stock: {product.stock} units
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, color: '#35C5F0' }}
                    >
                      {formatPrice(product.price)}
                    </Typography>
                    {product.originalPrice && (
                      <Typography
                        variant="body2"
                        sx={{
                          textDecoration: 'line-through',
                          color: '#999',
                        }}
                      >
                        {formatPrice(product.originalPrice)}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="textSecondary">
                    Sales: {product.sales} units
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEditProduct(product)}
                      sx={{ color: '#35C5F0' }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleToggleProductStatus(product.id)}
                      sx={{
                        color: product.status === 'ACTIVE' ? '#FF7F2F' : '#4CAF50',
                      }}
                    >
                      {product.status === 'ACTIVE' ? (
                        <VisibilityOffIcon fontSize="small" />
                      ) : (
                        <VisibilityIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>
        ))}
      </Box>

      {/* Edit Product Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Product Information</DialogTitle>
        <DialogContent>
          {selectedProduct && (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Product Name"
                defaultValue={selectedProduct.name}
                fullWidth
              />
              <TextField
                label="Description"
                defaultValue={selectedProduct.description}
                multiline
                rows={3}
                fullWidth
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Price"
                  type="number"
                  defaultValue={selectedProduct.price}
                  fullWidth
                />
                <TextField
                  label="Original Price"
                  type="number"
                  defaultValue={selectedProduct.originalPrice || ''}
                  fullWidth
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    defaultValue={selectedProduct.category}
                    label="Category"
                  >
                    {categories.map((category) => (
                      <MenuItem key={category} value={category}>{category}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Stock Quantity"
                  type="number"
                  defaultValue={selectedProduct.stock}
                  fullWidth
                />
              </Box>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  defaultValue={selectedProduct.status}
                  label="Status"
                >
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="INACTIVE">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{
              backgroundColor: '#35C5F0',
              '&:hover': { backgroundColor: '#2ba3cc' },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminProducts;